import logo from '@/assets/logo-CRP.png'
import { useColorModeValue } from '@/components/ui/color-mode'
import { ROUTES } from '@/constants/routes'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { uit } from '@/theme'
import {
    Box,
    Flex,
    IconButton,
    Menu,
    Portal,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    FiChevronDown,
    FiChevronUp,
    FiClipboard,
    FiHome,
    FiMenu,
    FiSettings,
    FiShield,
} from 'react-icons/fi'
import { NavLink, useLocation } from 'react-router-dom'

/**
 * Sidebar Navigation Component
 * Collapsible sidebar with modern design and brand colors
 * Uses centralized UI constants from theme/foundations.js
 */
export function Sidebar({ isOpen, onToggle }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const location = useLocation()
  const [expandedSections, setExpandedSections] = useState({})
  const bgColor = useColorModeValue('white', 'gray.900')
  const borderColor = useColorModeValue('#DFE1E6', 'gray.700')
  const activeBg = useColorModeValue('#DEEBFF', 'brand.900')
  const activeColor = useColorModeValue('#0052CC', 'brand.300')
  const hoverBg = useColorModeValue('#F4F5F7', 'gray.800')
  const logoBg = useColorModeValue('brand.500', 'brand.600')
  const logoTextColor = useColorModeValue('#0052CC', 'brand.300')

  // Main navigation items (top section)
  const mainNavItems = [
    { path: ROUTES.DASHBOARD, icon: FiHome, label: t('navigation.dashboard') },
    { path: ROUTES.LEADS, icon: FiClipboard, label: t('navigation.leads') },
  ]

  // Bottom navigation items with optional submenu (superadmin only)
  const bottomNavItems = []

  if (user?.is_superuser) {
    // Add Administration section
    bottomNavItems.push({
      key: 'administration',
      icon: FiShield,
      label: t('navigation.administration'),
      submenu: [
        { path: ROUTES.USERS, label: t('navigation.users') },
        { path: ROUTES.ACTIVITY_LOGS, label: t('navigation.audit') },
      ]
    })

    // Add Settings section
    bottomNavItems.push({
      key: 'settings',
      icon: FiSettings,
      label: t('navigation.settings'),
      submenu: [
        { path: ROUTES.SETTINGS_GLOBAL, label: t('navigation.settingsGlobal') },
        { path: ROUTES.SETTINGS_DEVELOPERS, label: t('navigation.settingsDevelopers') },
        { path: ROUTES.SETTINGS_RESIDENTIAL_COMPLEXES, label: t('navigation.settingsResidentialComplexes') },
        { path: ROUTES.SETTINGS_STREET_TYPES, label: t('navigation.settingsStreetTypes') },
        { path: ROUTES.SETTINGS_DISTRICTS_BIG, label: t('navigation.settingsDistrictsBig') },
        { path: ROUTES.SETTINGS_DISTRICTS, label: t('navigation.settingsDistricts') },
        { path: ROUTES.SETTINGS_SUBWAY, label: t('navigation.settingsSubway') },
        { path: ROUTES.SETTINGS_STREETS, label: t('navigation.settingsStreets') },
        { path: ROUTES.SETTINGS_PAYMENT_PACKAGES, label: t('navigation.settingsPaymentPackages') },
      ]
    })
  }

  // Auto-expand sections based on current route
  useEffect(() => {
    const newExpandedSections = {}

    bottomNavItems.forEach((item) => {
      if (item.submenu) {
        // Check if any submenu item matches current path
        const hasActiveSubmenu = item.submenu.some(
          (subItem) => location.pathname === subItem.path
        )
        if (hasActiveSubmenu) {
          newExpandedSections[item.key] = true
        }
      }
    })

    setExpandedSections(prev => ({ ...prev, ...newExpandedSections }))
  }, [location.pathname, user?.is_superuser])

  const toggleSection = (key) => {
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const handleSubmenuClick = () => {
    // Close all sections when a submenu item is clicked
    setExpandedSections({})
  }

  return (
    <Box
      as="nav"
      pos="fixed"
      left={ 0 }
      top={ 0 }
      h="100vh"
      bg={ bgColor }
      borderRight="1px"
      borderColor={ borderColor }
      w={ isOpen ? uit.layout.sidebar.widthOpen : uit.layout.sidebar.widthClosed }
      transition={`width ${uit.animations.transition.slow} ${uit.animations.timing.smooth}`}
      zIndex="sticky"
      overflowX="hidden"
      overflowY="auto"
      display="flex"
      flexDirection="column"
    >
      {/* Logo Section */}
      <Flex
        h={uit.layout.header.height}
        align="center"
        justify={ isOpen ? 'flex-start' : 'center' }
        px={ 4 }
        borderBottom="1px"
        borderColor={ borderColor }
        gap={ 3 }
      >
        {isOpen && (
          <img
            src={logo}
            alt="Broker24 Logo"
            style={{ height: '40px', width: 'auto' }}
          />
        )}
        <Box flex="1" />
        <IconButton
          onClick={ onToggle }
          variant="ghost"
          size="md"
          aria-label={t('navigation.toggleSidebar')}
          borderRadius="md"
        >
          <Box as={ FiMenu } />
        </IconButton>
      </Flex>

      {/* Main Navigation Items */}
      <VStack spacing={ 1 } align="stretch" px={ 0 } py={ 4 } flex={ 1 }>
        {mainNavItems.map((item) => (
          <Tooltip.Root key={ item.path } disabled={ isOpen } positioning={{ placement: 'right' }}>
            <Tooltip.Trigger asChild>
              <NavLink to={ item.path }>
                {({ isActive }) => (
                  <Flex
                    align="center"
                    px={ 3 }
                    py={ 2.5 }
                    mx={ 2 }
                    borderRadius="sm"
                    bg={ isActive ? activeBg : 'transparent' }
                    color={ isActive ? activeColor : '#172B4D' }
                    _hover={{ bg: isActive ? activeBg : hoverBg }}
                    cursor="pointer"
                    transition={uit.animations.transition.base}
                    gap={ 3 }
                    position="relative"
                    borderLeft={isActive ? '3px solid' : 'none'}
                    borderLeftColor={isActive ? '#0052CC' : 'transparent'}
                  >
                    <Box
                      as={ item.icon }
                      boxSize={uit.navigation.navItem.iconSize}
                      flexShrink={ 0 }
                      color={ isActive ? activeColor : '#5E6C84' }
                    />
                    {isOpen && (
                      <Text
                        fontSize="14px"
                        fontWeight={ isActive ? '600' : '500' }
                        whiteSpace="nowrap"
                        opacity={ isOpen ? 1 : 0 }
                        transition="opacity 0.2s"
                      >
                        { item.label }
                      </Text>
                    )}
                  </Flex>
                )}
              </NavLink>
            </Tooltip.Trigger>
            <Tooltip.Positioner>
              <Tooltip.Content>{ item.label }</Tooltip.Content>
            </Tooltip.Positioner>
          </Tooltip.Root>
        ))}
      </VStack>

      {/* Bottom Navigation Items */}
      <VStack spacing={ 1 } align="stretch" px={ 0 } py={ 4 } borderTop="1px" borderColor={ borderColor }>
        {bottomNavItems.map((item) => {
          // Item with submenu
          if (item.submenu) {
            const isExpanded = expandedSections[item.key]

            // When sidebar is collapsed, show menu popup
            if (!isOpen) {
              return (
                <Menu.Root
                  key={ item.key }
                  positioning={{
                    placement: 'right-start',
                    gutter: 12
                  }}
                >
                  <Menu.Trigger asChild>
                    <Flex
                      align="center"
                      px={ 3 }
                      py={ 2.5 }
                      mx={ 2 }
                      borderRadius="sm"
                      _hover={{ bg: hoverBg }}
                      cursor="pointer"
                      transition={uit.animations.transition.base}
                      gap={ 3 }
                    >
                      <Box
                        as={ item.icon }
                        boxSize={uit.navigation.navItem.iconSize}
                        flexShrink={ 0 }
                        color="#5E6C84"
                      />
                    </Flex>
                  </Menu.Trigger>
                  <Portal>
                    <Menu.Positioner>
                      <Menu.Content
                        minW="240px"
                        py={ 2 }
                        bg={ bgColor }
                        borderColor={ borderColor }
                        shadow="lg"
                        zIndex="dropdown"
                      >
                        {item.submenu.map((subItem) => (
                          <Menu.Item
                            key={ subItem.path }
                            value={ subItem.path }
                            asChild
                          >
                            <NavLink to={ subItem.path } onClick={handleSubmenuClick}>
                              {({ isActive }) => (
                                <Box
                                  px={ 4 }
                                  py={ 2 }
                                  w="100%"
                                  borderRadius="sm"
                                  bg={ isActive ? activeBg : 'transparent' }
                                  _hover={{ bg: isActive ? activeBg : hoverBg }}
                                  transition={uit.animations.transition.base}
                                >
                                  <Text
                                    fontSize="14px"
                                    fontWeight={ isActive ? '600' : '500' }
                                    color={ isActive ? activeColor : '#172B4D' }
                                  >
                                    { subItem.label }
                                  </Text>
                                </Box>
                              )}
                            </NavLink>
                          </Menu.Item>
                        ))}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Portal>
                </Menu.Root>
              )
            }

            // When sidebar is open, show expandable section
            return (
              <Box key={ item.key }>
                <Tooltip.Root disabled={ isOpen } positioning={{ placement: 'right' }}>
                  <Tooltip.Trigger asChild>
                    <Flex
                      align="center"
                      px={ 3 }
                      py={ 2.5 }
                      mx={ 2 }
                      borderRadius="sm"
                      _hover={{ bg: hoverBg }}
                      cursor="pointer"
                      transition={uit.animations.transition.base}
                      gap={ 3 }
                      onClick={() => toggleSection(item.key)}
                    >
                      <Box
                        as={ item.icon }
                        boxSize={uit.navigation.navItem.iconSize}
                        flexShrink={ 0 }
                        color="#5E6C84"
                      />
                      {isOpen && (
                        <>
                          <Text
                            fontSize="14px"
                            fontWeight="500"
                            whiteSpace="nowrap"
                            flex={ 1 }
                          >
                            { item.label }
                          </Text>
                          <Box
                            as={ isExpanded ? FiChevronUp : FiChevronDown }
                            boxSize="16px"
                            color="#5E6C84"
                          />
                        </>
                      )}
                    </Flex>
                  </Tooltip.Trigger>
                  <Tooltip.Positioner>
                    <Tooltip.Content>{ item.label }</Tooltip.Content>
                  </Tooltip.Positioner>
                </Tooltip.Root>

                {/* Submenu items */}
                {isExpanded && isOpen && (
                  <VStack spacing={ 0 } align="stretch" mt={ 1 }>
                    {item.submenu.map((subItem) => (
                      <NavLink key={ subItem.path } to={ subItem.path } onClick={handleSubmenuClick}>
                        {({ isActive }) => (
                          <Flex
                            align="center"
                            px={ 3 }
                            py={ 2 }
                            ml={ 6 }
                            mr={ 2 }
                            borderRadius="sm"
                            bg={ isActive ? activeBg : 'transparent' }
                            color={ isActive ? activeColor : '#172B4D' }
                            _hover={{ bg: isActive ? activeBg : hoverBg }}
                            cursor="pointer"
                            transition={uit.animations.transition.base}
                            borderLeft={ isActive ? '3px solid' : 'none' }
                            borderLeftColor={ isActive ? '#0052CC' : 'transparent' }
                          >
                            <Text
                              fontSize="13px"
                              fontWeight={ isActive ? '600' : '500' }
                              whiteSpace="nowrap"
                            >
                              { subItem.label }
                            </Text>
                          </Flex>
                        )}
                      </NavLink>
                    ))}
                  </VStack>
                )}
              </Box>
            )
          }

          // Regular item without submenu
          return (
            <Tooltip.Root key={ item.path } disabled={ isOpen } positioning={{ placement: 'right' }}>
              <Tooltip.Trigger asChild>
                <NavLink to={ item.path }>
                  {({ isActive }) => (
                    <Flex
                      align="center"
                      px={ 3 }
                      py={ 2.5 }
                      mx={ 2 }
                      borderRadius="sm"
                      bg={ isActive ? activeBg : 'transparent' }
                      color={ isActive ? activeColor : '#172B4D' }
                      _hover={{ bg: isActive ? activeBg : hoverBg }}
                      cursor="pointer"
                      transition={uit.animations.transition.base}
                      gap={ 3 }
                      position="relative"
                      borderLeft={isActive ? '3px solid' : 'none'}
                      borderLeftColor={isActive ? '#0052CC' : 'transparent'}
                    >
                      <Box
                        as={ item.icon }
                        boxSize={uit.navigation.navItem.iconSize}
                        flexShrink={ 0 }
                        color={ isActive ? activeColor : '#5E6C84' }
                      />
                      {isOpen && (
                        <Text
                          fontSize="14px"
                          fontWeight={ isActive ? '600' : '500' }
                          whiteSpace="nowrap"
                          opacity={ isOpen ? 1 : 0 }
                          transition="opacity 0.2s"
                        >
                          { item.label }
                        </Text>
                      )}
                    </Flex>
                  )}
                </NavLink>
              </Tooltip.Trigger>
              <Tooltip.Positioner>
                <Tooltip.Content>{ item.label }</Tooltip.Content>
              </Tooltip.Positioner>
            </Tooltip.Root>
          )
        })}
      </VStack>
    </Box>
  )
}

export default Sidebar
